/**
 * CSV Import Parser
 *
 * RFC 4180-compliant CSV parser with type validation and ArCell construction.
 * Handles quoted fields, escaped quotes, newlines within quotes, compound
 * field reconstruction, and per-cell error collection.
 *
 * `date` and `dateTime` are separate `CsvDataType`s because they anchor
 * differently: a `date` column lands on start of the day in `defaultTimeZone`, a
 * `dateTime` column on the browser-local instant (SYD-2507). `coerceDate` passes
 * the `CsvDataType` straight through as the field type id, so the two stay in step.
 */

import type { Timestamp } from 'firebase/firestore';
import type { ArCell, ArManualEntryValue, TypeId } from '#types/System.Artifacts.types';
import { timestampFromDateString } from '@/dates';
import type { CsvFlatColumn } from './flattenColumnsForCsv';

type CsvImportError = {
	row: number;
	column: string;
	message: string;
};

type CsvParseResult = {
	rows: Array<Record<TypeId, ArCell>>;
	errors: CsvImportError[];
	warnings: string[];
	totalRows: number;
};

const MAX_IMPORT_ROWS = 100_000;

// ── RFC 4180 Parser ──────────────────────────────────────────────

/**
 * One parsed row, carrying the 1-indexed file line it started on. All-blank rows are
 * dropped, so a row's position in this array is not its line: callers that report an
 * error to the operator must quote `line`, or the numbers drift past every blank line.
 * A quoted field may span newlines, which is why the line is tracked rather than counted.
 */
type CsvRow = {
	line: number;
	cells: string[];
};

function parseCsvRows(text: string): CsvRow[] {
	const rows: CsvRow[] = [];
	const chars = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
	const len = chars.length;

	let cells: string[] = [];
	let field = '';
	let inQuotes = false;
	let i = 0;
	let line = 1;
	let rowLine = 1;

	while (i < len) {
		const ch = chars[i];

		if (inQuotes) {
			if (ch === '"') {
				if (i + 1 < len && chars[i + 1] === '"') {
					field += '"';
					i += 2;
				} else {
					inQuotes = false;
					i++;
				}
			} else {
				if (ch === '\n') line++;
				field += ch;
				i++;
			}
		} else {
			if (ch === '"') {
				inQuotes = true;
				i++;
			} else if (ch === ',') {
				cells.push(field);
				field = '';
				i++;
			} else if (ch === '\n') {
				cells.push(field);
				field = '';
				if (cells.some((f) => f.trim().length > 0)) {
					rows.push({ line: rowLine, cells });
				}
				cells = [];
				line++;
				rowLine = line;
				i++;
			} else {
				field += ch;
				i++;
			}
		}
	}

	cells.push(field);
	if (cells.some((f) => f.trim().length > 0)) {
		rows.push({ line: rowLine, cells });
	}

	return rows;
}

// ── Type Coercion ────────────────────────────────────────────────

function coerceNumber(raw: string): { value: number } | { error: string } {
	const trimmed = raw.trim();
	const isAccountingNegative = /^\([\d$,. ]+\)$/.test(trimmed);
	const stripped = isAccountingNegative ? trimmed.slice(1, -1) : trimmed;
	const cleaned = stripped.replace(/[$,]/g, '').trim();
	const num = parseFloat(cleaned);
	if (Number.isNaN(num)) return { error: `"${raw}" is not a valid number` };
	return { value: isAccountingNegative ? -num : num };
}

function coerceBoolean(raw: string): { value: boolean } | { error: string } {
	const lower = raw.trim().toLowerCase();
	if (lower === 'true' || lower === 'yes' || lower === '1') return { value: true };
	if (lower === 'false' || lower === 'no' || lower === '0') return { value: false };
	return { error: `"${raw}" is not a valid boolean (use true/false, yes/no, or 1/0)` };
}

function coerceDate(
	raw: string,
	dataType: 'date' | 'dateTime',
): { value: Timestamp } | { error: string } {
	const timestamp = timestampFromDateString(raw.trim(), dataType);
	if (!timestamp) return { error: `"${raw}" is not a valid date` };
	return { value: timestamp };
}

function coerceSubFieldValue(
	raw: string,
	dataType: CsvFlatColumn['dataType'],
): unknown {
	switch (dataType) {
		case 'number': {
			const result = coerceNumber(raw);
			return 'error' in result ? null : result.value;
		}
		case 'boolean': {
			const result = coerceBoolean(raw);
			return 'error' in result ? null : result.value;
		}
		case 'date':
		case 'dateTime': {
			const result = coerceDate(raw, dataType);
			return 'error' in result ? null : result.value;
		}
		default:
			return raw;
	}
}

// ── Header Matching ──────────────────────────────────────────────

type ColumnMatch = {
	csvIndex: number;
	flatColumn: CsvFlatColumn;
};

function matchHeaders(
	csvHeaders: string[],
	flatColumns: CsvFlatColumn[],
): { matches: ColumnMatch[]; warnings: string[] } {
	const importable = flatColumns.filter((c) => c.importable);
	const headerMap = new Map(importable.map((c) => [c.header, c]));
	const headerMapLower = new Map(importable.map((c) => [c.header.toLowerCase(), c]));

	const matches: ColumnMatch[] = [];
	const warnings: string[] = [];

	for (let i = 0; i < csvHeaders.length; i++) {
		const header = csvHeaders[i].trim();
		const match = headerMap.get(header) ?? headerMapLower.get(header.toLowerCase());
		if (match) {
			matches.push({ csvIndex: i, flatColumn: match });
		} else if (header.length > 0) {
			warnings.push(`Unmatched CSV header: "${header}"`);
		}
	}

	return { matches, warnings };
}

// ── Compound Field Reconstruction ────────────────────────────────

function setNestedValue(
	obj: Record<string, unknown>,
	path: string,
	value: unknown,
): void {
	const parts = path.split('.');
	let current: Record<string, unknown> = obj;
	for (let i = 0; i < parts.length - 1; i++) {
		const key = parts[i];
		if (!(key in current) || typeof current[key] !== 'object' || current[key] === null) {
			current[key] = {};
		}
		current = current[key] as Record<string, unknown>;
	}
	current[parts[parts.length - 1]] = value;
}

// ── Main Parse Function ──────────────────────────────────────────

function parseCsvImport(
	csvText: string,
	flatColumns: CsvFlatColumn[],
): CsvParseResult {
	const parsed = parseCsvRows(csvText).map((row) => row.cells);

	if (parsed.length === 0) {
		return { rows: [], errors: [], warnings: ['CSV file is empty'], totalRows: 0 };
	}

	const csvHeaders = parsed[0];
	const dataRows = parsed.slice(1);
	const totalRows = dataRows.length;

	if (totalRows === 0) {
		return { rows: [], errors: [], warnings: ['CSV file has headers but no data rows'], totalRows: 0 };
	}

	const { matches, warnings } = matchHeaders(csvHeaders, flatColumns);

	if (totalRows > MAX_IMPORT_ROWS) {
		return {
			rows: [],
			errors: [],
			warnings: [...warnings, `CSV has ${totalRows.toLocaleString()} rows, exceeding the limit of ${MAX_IMPORT_ROWS.toLocaleString()}`],
			totalRows,
		};
	}

	if (matches.length === 0) {
		return { rows: [], errors: [], warnings: [...warnings, 'No matching columns found'], totalRows };
	}

	const errors: CsvImportError[] = [];
	const rows: Array<Record<TypeId, ArCell>> = [];

	for (let rowIdx = 0; rowIdx < dataRows.length; rowIdx++) {
		const csvRow = dataRows[rowIdx];
		const rowNum = rowIdx + 2; // 1-indexed, skip header row

		const compoundAccumulator = new Map<TypeId, Record<string, unknown>>();
		const cells: Record<TypeId, ArCell> = {};

		for (const { csvIndex, flatColumn } of matches) {
			const raw = csvIndex < csvRow.length ? csvRow[csvIndex] : '';
			const trimmed = raw.trim();
			if (trimmed.length === 0) continue;

			const { typeId, subFieldPath, targetTypeId, dataType, header } = flatColumn;
			const cellKey = targetTypeId ?? typeId;

			if (dataType === 'skip') continue;

			if (subFieldPath) {
				const coerced = coerceSubFieldValue(trimmed, dataType);
				if (coerced === null) {
					errors.push({ row: rowNum, column: header, message: `"${trimmed}" is not valid for this field` });
					continue;
				}

				const compound = compoundAccumulator.get(typeId) ?? {};
				setNestedValue(compound, subFieldPath, coerced);
				compoundAccumulator.set(typeId, compound);
				continue;
			}

			switch (dataType) {
				case 'number': {
					const result = coerceNumber(trimmed);
					if ('error' in result) {
						errors.push({ row: rowNum, column: header, message: result.error });
					} else {
						cells[cellKey] = { value: result.value };
					}
					break;
				}
				case 'boolean': {
					const result = coerceBoolean(trimmed);
					if ('error' in result) {
						errors.push({ row: rowNum, column: header, message: result.error });
					} else {
						cells[cellKey] = { value: result.value };
					}
					break;
				}
				case 'date':
				case 'dateTime': {
					const result = coerceDate(trimmed, dataType);
					if ('error' in result) {
						errors.push({ row: rowNum, column: header, message: result.error });
					} else {
						cells[cellKey] = { value: result.value };
					}
					break;
				}
				case 'reference': {
					const manualEntry: ArManualEntryValue = { valueType: 'manual', value: trimmed };
					cells[cellKey] = { value: manualEntry };
					break;
				}
				default: {
					cells[cellKey] = { value: trimmed };
				}
			}
		}

		compoundAccumulator.forEach((compound, typeId) => {
			cells[typeId] = { value: compound as ArCell['value'] };
		});

		if (Object.keys(cells).length > 0) {
			rows.push(cells);
		}
	}

	return { rows, errors, warnings, totalRows };
}

export { parseCsvImport, parseCsvRows };
export type { CsvParseResult, CsvImportError, CsvRow };
